// The Auto-fix card on a feedback request: where the runner (a Claude Code scheduled task on a staff Mac) is,
// links to its draft PR and review artifact, and Stop / Allow. The runner writes only internal notes and this
// record (feedback_autofix_runs, migration 019), so nothing here reaches the firm.
import { useState } from 'react';
import { Bot, Check, Circle, ExternalLink } from 'lucide-react';
import type { AutofixState } from '@upm/domain';
import { api, ApiError } from '../../lib/api';
import { fmtDateTime } from '../../lib/format';
import { Button, ButtonA, Card, Pill, cx, type PillTone } from '../../ui';
import type { RequestDetail } from './types';

const STEPS: Array<{ state: AutofixState; label: string }> = [
  { state: 'intake', label: 'Intake' },
  { state: 'root_cause', label: 'Root cause' },
  { state: 'fix', label: 'Fix' },
  { state: 'review', label: 'Review artifact' },
];
const ORDER: AutofixState[] = ['intake', 'root_cause', 'fix', 'review', 'waiting', 'merged', 'rejected', 'failed'];

const STATUS: Record<AutofixState, [string, PillTone]> = {
  intake: ['Working', 'info'], root_cause: ['Working', 'info'], fix: ['Working', 'info'], review: ['Working', 'info'],
  waiting: ['Waiting for you', 'warn'], merged: ['Merged', 'ok'], rejected: ['Rejected', 'neutral'], failed: ['Failed', 'bad'],
};
const DECISION: Partial<Record<AutofixState, string>> = {
  waiting: 'Approve, request changes or reject on the review artifact',
  merged: 'Approved and merged',
  rejected: 'Rejected; the request stays open for a person',
  failed: 'The runner stopped with an error',
};

export function AutofixPanel({ request, onSaved, onError }: { request: RequestDetail; onSaved: (r: RequestDetail) => void; onError: (m: string) => void }) {
  const [busy, setBusy] = useState(false);
  const run = request.autofix;
  if (!run) return null;
  const reached = ORDER.indexOf(run.state);
  const stopped = !!run.stopped_at;
  const [label, tone] = stopped ? ['Stopped', 'neutral' as PillTone] : STATUS[run.state];
  const decided = run.steps.merged ?? run.steps.rejected ?? run.steps.failed ?? run.steps.waiting;

  const toggle = async () => {
    setBusy(true);
    try { onSaved((await api.post<{ request: RequestDetail }>(`/api/admin/feedback/${request.id}/autofix/${stopped ? 'allow' : 'stop'}`, {})).request); }
    catch (e) { onError(e instanceof ApiError ? e.message : 'Could not change auto-fix on this request.'); }
    finally { setBusy(false); }
  };

  return (
    <Card className="flex flex-col gap-3.5 border-2 border-brand p-5 text-sm">
      <div className="flex items-center justify-between gap-2">
        <h2 className="m-0 flex items-center gap-1.5 text-[15px] font-bold"><Bot aria-hidden size={16} /> Auto-fix</h2>
        <Pill tone={tone} dot>{label}</Pill>
      </div>
      <ol className="m-0 flex list-none flex-col gap-2.5 p-0">
        {STEPS.map((s, i) => {
          const step = run.steps[s.state];
          const done = i < reached;
          const current = i === reached;
          return (
            <li key={s.state} className="flex items-start gap-2.5">
              <StepIcon done={done} current={current && !stopped} />
              <span className={cx(!done && !current && 'text-muted')}>
                <span className="font-semibold">{s.label}</span>
                {step && <span className="block text-[13px] text-muted">{step.detail ? `${step.detail} · ` : ''}{fmtDateTime(step.at)}</span>}
              </span>
            </li>
          );
        })}
        <li className="flex items-start gap-2.5">
          <StepIcon done={run.state === 'merged' || run.state === 'rejected'} current={run.state === 'waiting' && !stopped} />
          <span className={cx(reached < ORDER.indexOf('waiting') && 'text-muted')}>
            <span className="font-semibold">Your decision</span>
            {DECISION[run.state] && (
              <span className="block text-[13px] text-muted">{decided?.detail ?? DECISION[run.state]}{decided ? ` · ${fmtDateTime(decided.at)}` : ''}</span>
            )}
          </span>
        </li>
      </ol>
      {run.review_url && (
        <ButtonA variant="primary" href={run.review_url} target="_blank" rel="noreferrer"><ExternalLink aria-hidden size={16} /> Open review artifact</ButtonA>
      )}
      {run.pr_url && <ButtonA variant="link" size="sm" href={run.pr_url} target="_blank" rel="noreferrer" className="w-fit">Open the draft PR</ButtonA>}
      {stopped && <p className="m-0 text-[13px] text-muted">Stopped by {run.stopped_by_name ?? 'staff'} · {fmtDateTime(run.stopped_at!)}</p>}
      <Button variant="secondary" disabled={busy} onClick={() => void toggle()}>
        {stopped ? 'Allow auto-fix again' : 'Stop auto-fix for this request'}
      </Button>
      <p className="m-0 text-[13px] text-muted">The runner adds internal notes only. Nothing goes to the firm from here.</p>
    </Card>
  );
}

function StepIcon({ done, current }: { done: boolean; current: boolean }) {
  if (done) return <Check aria-label="Done" size={18} className="mt-0.5 flex-none text-ok" strokeWidth={2.5} />;
  return <Circle aria-label={current ? 'In progress' : 'Not started'} size={18} className={cx('mt-0.5 flex-none', current ? 'fill-warn-dot text-warn-dot' : 'text-line-btn')} />;
}
